import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { NodeType } from '@/api/types/pt_enums'
import { getLoginInfo, setLoginInfo } from '@/utils/platform'

import type { LoginInfo, PtAccount, RolePrivilegeInfo } from '@/api/types/pt_entities'

export const usePlatformStore = defineStore('platform', () => {
  const loginInfo = ref<LoginInfo>({})
  const selfInfo = ref<PtAccount>({})
  const isLogined = computed(() => !!selfInfo.value?.id)
  const privileges = ref<Record<string, RolePrivilegeInfo>>({})

  async function loginSuccess(info: LoginInfo) {
    selfInfo.value = await apiGetAccount({ accountId: 0 })
    loginInfo.value = info
    await loadPrivileges()
  }

  async function initSelfInfo(redirectToLogin: boolean = true) {
    if (getLoginInfo().accessToken) {
      const info = await apiGetAccount({ accountId: 0, options: { redirectToLogin, silent: true } })
      selfInfo.value = info ?? {}
    }
  }

  async function logout() {
    try {
      await apiLogout()
    } finally {
      selfInfo.value = {}
      setLoginInfo(undefined)
      loginInfo.value = {}
      await loadPrivileges()
    }
  }

  async function loadPrivileges() {
    const privilegeList = await apiGetSelfPrivileges({ type: 'api' })
    const newPrivileges: Record<string, RolePrivilegeInfo> = {}
    privilegeList.forEach((privilege) => initPrivilege(newPrivileges, privilege))
    privileges.value = newPrivileges
  }

  function initPrivilege(newPrivileges: Record<string, RolePrivilegeInfo>, privilege: RolePrivilegeInfo) {
    if (privilege.nodeType === NodeType.LEAF.value) {
      newPrivileges[privilege.name!] = privilege
    } else if (privilege.children && privilege.children.length > 0) {
      privilege.children.forEach((child) => initPrivilege(newPrivileges, child))
    }
  }

  return {
    loginInfo,
    selfInfo,
    privileges,
    isLogined,
    initSelfInfo,
    loadPrivileges,
    loginSuccess,
    logout
  }
})
